import { useMemo } from "react";
import { useSelector } from "react-redux";

import { useEnsureCorrespondents } from "./useEnsureReferenceData";

/**
 * useCorrespondentOptions — maps the cached correspondent objects (fetched by
 * useFetchedSelectionsRequestsHook().getCorrespondents into
 * `state.fetchedSelections.correspondents`) into select-field options:
 *   [{ value: <correspondentId>, label: <name> }]
 *
 * Shaped for SelectAutoInput / RffSelectAutocompleteField, which store
 * `option.value` and display `option.label`. So a bound field persists the
 * correspondent id. Returns [] until the list has loaded.
 */
export const useCorrespondentOptions = () => {
  // Ensure the correspondent list is loaded (cache-gated) whenever a correspondent
  // picker is on screen — the create-loan form, update-loan modal, and drilldown
  // all build their options from this hook, so the fetch follows the field.
  useEnsureCorrespondents();
  const correspondents = useSelector(
    (state) => state.fetchedSelections?.correspondents,
  );

  return useMemo(() => {
    if (!Array.isArray(correspondents)) return [];
    return correspondents
      .map((c) => {
        const value = c?._id ?? c?.id;
        // Backend list is keyed/sorted by companyName; prefer it for the label.
        const label =
          c?.companyName ?? c?.name ?? c?.correspondentName ?? value;
        if (!value) return null;
        return { value, label: String(label) };
      })
      .filter(Boolean);
  }, [correspondents]);
};

export default useCorrespondentOptions;
